import URI from "urijs";
import $ from "jquery";

import {withLocaleInURI} from "../internationalization/i18nURI";
import JSONWebToken from "../utils/JSONWebToken";

// Right now, this uses the $.ajax() call for XHRs.  As long as we are standardized on
// using this function and calling .done()/.fail() promise functions after, it should
// be easy enough to upgrade to NextJS SWR (fetch) tool in the future.
export function ConnectionFactory(orgId = '', url: string, settings: {
    [key: string]: any
} = {}): JQueryXHR {
    // For settings information, see: https://api.jquery.com/Jquery.ajax/

    // the goal of this JWT is for per-org permission checking on the backend. it should ALWAYS reflect the
    // current organization ID. some routes may require their own permission checking (such as editing a
    // contact modal), but that's the concern of the backend. dumb frontend, smart backend.
    const jwt = new JSONWebToken().encode(orgId);
    const headers = {
        "api-key": jwt,
    }
    const ajaxSettings = {
        url: url,
        method: "GET",
        dataType: "json",
        converters: {
            "text json" : (response) => {
                // This is here because jQuery errors on empty responses when dataType is set to
                // JSON because it's not "valid".  Instead, we want to just return a JSON value
                // of null.
                return (response == "") ? null : JSON.parse(response);
            },
        },
        // send api-key so we can set the correct organization in PHP off of the JWT.
        headers: settings.headers ? {...settings.headers, ...headers} : headers,
        ...settings,
    }
    return $.ajax(ajaxSettings);
}

export function YiiUrlFactory(path: string, locale: string): string {
    if (!locale) {
        console.warn(`No locale set for URL ${path}`);
    }
    return withLocaleInURI(path, locale);
}

export class AdaController {
    static prefix = "/cms/ada"

    static evaluate(locale: string): string {
        const path = URI.joinPaths(this.prefix, "evaluate").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class AdminController {
    static prefix = "/cms/admin"

    static organizationSettings(locale: string): string {
        const path = URI.joinPaths(this.prefix, "organizationSettings").toString();
        return YiiUrlFactory(path, locale);
    }

    static updateOrganizationSettings(locale: string): string {
        const path = URI.joinPaths(this.prefix, "updateOrganizationSettings").toString();
        return YiiUrlFactory(path, locale);
    }

    static uploadImage(locale: string): string {
        const path = URI.joinPaths(this.prefix, "uploadImage").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class ApiController {
    static rootPrefix = "/_!_API_!_";
    static version = "2";

    static rootTypes = {
        ORGANIZATION: "organization",
        PERSON: "people",
        EVENTS: "events",
    };

    static fieldNames = {
        BLOCK: '_blockHTML',
        MODAL: '_modalHTML',
        AD: '_adHTML',
        GRID_STATES: '_gridStates',
        BLOCKOBJ: '_blockObj',
    };

    // This function is necessary because URIjs automatically encodes _!_ to _%21_.  Grrr.
    static fixEncoding(path: string): string {
        return path.replaceAll("_%21_", "_!_");
    }

    static viewOrganization(locale: string, orgId: string, blockId?: string, itemId?: string , fields?: string[]): string {
        let path = URI.joinPaths(this.rootPrefix, this.version, this.rootTypes.ORGANIZATION, orgId);
        if (blockId) {
            path = URI.joinPaths(path, 'blocks', blockId)

            if (itemId) {
                path = URI.joinPaths(path, 'items', itemId)
            }
        }

        if (fields) {
            path = path.search({fields: fields.join(",")})
        }
        return this.fixEncoding(YiiUrlFactory(path.toString(), locale));
    }
}

export class AuthenticationController {
    static prefix = "/cms/authentication";
    static rootPrefix = "/";

    static facebook(locale: string, jwt: string, fetchAccounts: boolean): string {
        const path = URI.joinPaths(this.prefix, "facebookLogin")
            .search({state: jwt, fetchAccounts})
            .toString();
        return YiiUrlFactory(path, locale);
    }

    static google(locale: string, jwt: string): string {
        const path = URI.joinPaths(this.prefix, "googleLogin")
            .search({state: jwt})
            .toString();
        return YiiUrlFactory(path, locale);
    }

    static login(locale: string): string {
        const path = URI.joinPaths(this.rootPrefix, "login").toString();
        return YiiUrlFactory(path, locale);
    }

    static logout(locale: string): string {
        const path = URI.joinPaths(this.rootPrefix, "logout").toString();
        return YiiUrlFactory(path, locale);
    }

    static office365(locale: string, jwt: string): string {
        const path = URI.joinPaths(this.prefix, "office365Login")
            .search({state: jwt})
            .toString();
        return YiiUrlFactory(path, locale);
    }

    static office365AdminSetupLogin(locale: string): string {
        const path = URI.joinPaths(this.prefix, "office365AdminSetupLogin").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class CalendarController {
    static prefix = "/cms/calendar";

    static editCalendar(locale: string): string {
        const path = URI.joinPaths(this.prefix, "editcalendar").toString();
        return YiiUrlFactory(path, locale);
    }

    static getCalendarsList(locale: string): string {
        const path = URI.joinPaths(this.prefix, "getcalendarslist").toString();
        return YiiUrlFactory(path, locale);
    }

    static getListing(locale: string): string {
        const path = URI.joinPaths(this.prefix, "getlisting").toString();
        return YiiUrlFactory(path, locale);
    }

    static removeCalendar(locale: string): string {
        const path = URI.joinPaths(this.prefix, "removecalendar").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class ClassroomController {
    static prefix = "/cms/classroom";

    static create(locale: string): string {
        const path = URI.joinPaths(this.prefix, "create").toString();
        return YiiUrlFactory(path, locale);
    }

    static delete(locale: string, classId: string): string {
        const path = URI.joinPaths(this.prefix, classId, "delete").toString();
        return YiiUrlFactory(path, locale);
    }

    static updateLinkedClassroom(locale: string): string {
        const path = URI.joinPaths(this.prefix, "updateLinkedClassroomLink").toString();
        return YiiUrlFactory(path, locale);
    }

    static updateExternalClassroomLink(locale: string): string {
        const path = URI.joinPaths(this.prefix, "updateExternalClassroomLink").toString();
        return YiiUrlFactory(path, locale);
    }

    static getGoogleClassrooms(locale: string): string {
        const path = URI.joinPaths(this.prefix, "getGoogleClassrooms").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class DefaultController {
    static prefix = "/cms/default";

    static addGroupPermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "addGroupPermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static addPermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "addpermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static blockCreate(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "addBlock", blockId, "create").toString();
        return YiiUrlFactory(path, locale);
    }

    static blockDelete(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "deleteBlock", blockId, "delete").toString();
        return YiiUrlFactory(path, locale);
    }

    static blockFetch(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "getAsyncBlock", blockId).toString();
        return YiiUrlFactory(path, locale);
    }

    static blockUploadImage(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "uploadBlockImage", blockId).toString();
        return YiiUrlFactory(path, locale);
    }

    static blockSetHeaderTitle(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "setBlockHeaderTitle", blockId, "header").toString();
        return YiiUrlFactory(path, locale);
    }

    static changeGroupPermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "changeGroupPermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static changePermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "changePermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static getEditBlock(locale: string, blockType: string): string {
        const path = URI.joinPaths(this.prefix, "getEditBlock", blockType, "edit").toString();
        return YiiUrlFactory(path, locale);
    }

    static removePermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "removepermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static removeGroupPermissions(locale: string): string {
        const path = URI.joinPaths(this.prefix, "removeGroupPermissions").toString();
        return YiiUrlFactory(path, locale);
    }

    static searchGrids(locale: string, gridId: string): string {
        const path = URI.joinPaths(this.prefix, "searchGrids", gridId).toString();
        return YiiUrlFactory(path, locale);
    }

    static searchPermissionGroups(locale: string, q: string, identityProvider: undefined | string): string {
        const path = URI.joinPaths(this.prefix, "searchPermissionGroups")
            .search({q: q, identity_provider: identityProvider})
            .toString();
        return YiiUrlFactory(path, locale);
    }

    static submitEditBlock(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "getBlock", blockId).toString();
        return YiiUrlFactory(path, locale);
    }

    static updateGrid(locale: string, gridId: string): string {
        const path = URI.joinPaths(this.prefix, "updateGrid", gridId, "update").toString();
        return YiiUrlFactory(path, locale);
    }

    static toggleFollowing(locale: string, orgId: string): string {
        const path = URI.joinPaths(this.prefix, "toggleFollowOrganization", orgId).toString();
        return YiiUrlFactory(path, locale);
    }

    static blockCommand(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, "blockCommand", blockId, "command").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class LocationController {
    static prefix = "/cms/location";

    static create(locale: string, organizationId: string): string {
        // Used in PHP code: organizationModal.php
        const path = URI.joinPaths(this.prefix, "create/id", organizationId).toString();
        return YiiUrlFactory(path, locale);
    }

    static delete(locale: string, locationId: string): string {
        // Used in PHP code: organizationModal.php
        const path = URI.joinPaths(this.prefix, "delete/id", locationId).toString();
        return YiiUrlFactory(path, locale);
    }

    static save(locale: string): string {
        // Used in PHP code: organizationModal.php
        const path = URI.joinPaths(this.prefix, "save").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class NotificationController {
    static prefix = "/cms/notification"

    static editNotification(locale: string): string {
        const path = URI.joinPaths(this.prefix, "editnotification").toString();
        return YiiUrlFactory(path, locale);
    }

    static getNotificationsList(locale: string, orgId: string): string {
        const path = URI.joinPaths(this.prefix, "getnotificationslist")
            .search({organization_id: orgId})
            .toString();
        return YiiUrlFactory(path, locale);
    }

    static removeNotification(locale: string): string {
        const path = URI.joinPaths(this.prefix, "removenotification").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class OrganizationController {
    static prefix = "/cms/organization";
    static rootPrefix = "/cms";

    static fluidAccess(locale: string, orgType: "group" | "section", orgId: string): string {
        const path = URI.joinPaths(this.rootPrefix, orgType, orgId, "access").toString();
        return YiiUrlFactory(path, locale);
    }

    static fluidRestore(locale: string, orgType: "classroom" | "group" | "section" | "team", orgId: string): string {
        const path = URI.joinPaths(this.rootPrefix, orgType, orgId, "restore").toString();
        return YiiUrlFactory(path, locale);
    }

    static save(locale: string): string {
        // Used in PHP code: organizationModal.php
        const path = URI.joinPaths(this.prefix, "save").toString();
        return YiiUrlFactory(path, locale);
    }

    static updateLogo(locale: string): string {
        // Used in PHP code: organizationModal.php
        const path = URI.joinPaths(this.prefix, "updateLogo").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class PageController {
    static prefix = "/cms/page"

    static compare(locale: string, pageId: string, otherPageId: string): string {
        const path = URI.joinPaths(this.prefix, pageId, "compare", otherPageId).toString();
        return YiiUrlFactory(path, locale);
    }

    static image(locale: string): string {
        const path = URI.joinPaths(this.prefix, "saveImage").toString();
        return YiiUrlFactory(path, locale);
    }

    static update(locale: string, blockId: string): string {
        const path = URI.joinPaths(this.prefix, blockId, "update").toString();
        return YiiUrlFactory(path, locale);
    }

    static versions(locale: string, pageId: string): string {
        const path = URI.joinPaths(this.prefix, pageId, "version").toString();
        return YiiUrlFactory(path, locale);
    }

    static snippets(locale: string): string {
        const path = URI.joinPaths(this.prefix, "snippets").toString();
        return YiiUrlFactory(path, locale);
    }

    static restore(locale: string): string {
        const path = URI.joinPaths(this.prefix, "restore").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class PartialsController {
    static prefix = "/partials"

    static googleFilePicker(locale: string): string {
        const path = URI.joinPaths(this.prefix, "googleFilePicker").toString();
        return YiiUrlFactory(path, locale);
    }

    static getCollectionContactCsv(locale: string): string {
        const path = URI.joinPaths(this.prefix, "getCollectionContactCsv").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class SchoolController {
    static prefix = "/cms/school"

    static save(locale: string): string {
        const path = URI.joinPaths(this.prefix, "save").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class SearchController {
    static rootPrefix = "/";

    static search(locale: string): string {
        const path = URI.joinPaths(this.rootPrefix, "search").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class StaffController {
    static prefix = "/cms/staff"

    static changeDeleted(locale: string, userId: string): string {
        const path = URI.joinPaths(this.prefix, userId, "changeDeleted").toString();
        return YiiUrlFactory(path, locale);
    }

    static departments(locale: string, userId: string, action: ""|"create"|"delete"|"restore"|"update" = ""): string {
        const path = URI.joinPaths(this.prefix, userId, "departments", action).toString();
        return YiiUrlFactory(path, locale);
    }

    static save(locale: string): string {
        const path = URI.joinPaths(this.prefix, "saveStaff").toString();
        return YiiUrlFactory(path, locale);
    }

    static updatePicture(locale: string): string {
        const path = URI.joinPaths(this.prefix, "updatePicture").toString();
        return YiiUrlFactory(path, locale);
    }
}

export class UserController {
    static prefix = "/cms/user"

    static feed(locale: string): string {
        const path = URI.joinPaths(this.prefix, "feed").toString();
        return YiiUrlFactory(path, locale);
    }

    static following(locale: string): string {
        const path = URI.joinPaths(this.prefix, "following").toString();
        return YiiUrlFactory(path, locale);
    }

    static myCalendar(locale: string): string {
        const path = URI.joinPaths(this.prefix, "myCalendar").toString();
        return YiiUrlFactory(path, locale);
    }

    static settings(locale: string): string {
        const path = URI.joinPaths(this.prefix, "settings").toString();
        return YiiUrlFactory(path, locale);
    }

    static saveSettings(locale: string): string {
        const path = URI.joinPaths(this.prefix, "saveSettings").toString();
        return YiiUrlFactory(path, locale);
    }
}